import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNewBookingStore } from '../../../domain/booking/stores/useNewBookingsStore';
import { ApiErrorMessage } from '../../errors/ApiErrorMessage';
import { trackStartPayment } from '../shared/analytics/e-commerce/trackStartPayment';
import { BookingSummaryLayout } from '../shared/summary/BookingSummaryLayout';
import { usePaymentContext } from './PaymentContext';
import { PaymentMain } from './PaymentMain';
import { PaymentSubmit } from './submit/PaymentSubmit';
import { getTotalNewBookingPrice } from '../../../domain/booking/models/helpers/getTotalNewBookingPrice';
import { getShouldProceedWithoutPay } from '../../../domain/booking/models/helpers/getShouldProceedWithoutPay';
import { getOriginalGrandTotalPrice } from '../../../domain/booking/models/helpers/getOriginalGrandTotalPrice';

interface IPaymentLayoutProps {
	paymentError?: Error;
	sessionExpiredNextUrl: string;
	changeButtonUrl?: string;
}

export const PaymentLayout = observer(
	({
		paymentError,
		sessionExpiredNextUrl,
		changeButtonUrl,
	}: IPaymentLayoutProps) => {
		const { originalBooking, booking } = useNewBookingStore();
		const { newCard, microformInstance } = usePaymentContext();

		useEffect(() => {
			trackStartPayment(booking);
		}, []);

		const originalTotalPrice =
			originalBooking && getOriginalGrandTotalPrice(originalBooking);
		const totalPrice = getTotalNewBookingPrice(booking);
		const shouldProceedWithoutPay = getShouldProceedWithoutPay(
			originalTotalPrice,
			totalPrice,
		);

		// Wrap inline component in useCallback to make sure it's not re-mounted every render
		const WrappedPaymentSubmit = useCallback(
			() => <PaymentSubmit shouldProceedWithoutPay={shouldProceedWithoutPay} />,
			[newCard, microformInstance, shouldProceedWithoutPay],
		);

		return (
			<BookingSummaryLayout
				originalBooking={originalBooking}
				booking={booking}
				Submit={WrappedPaymentSubmit}
				changeButtonUrl={changeButtonUrl}
				hidePaymentSection={shouldProceedWithoutPay}
			>
				{!shouldProceedWithoutPay && <PaymentMain />}

				{paymentError && (
					<ApiErrorMessage
						error={paymentError}
						genericMessage="parking_payment_generic_error_message"
						sessionExpiredNextUrl={sessionExpiredNextUrl}
					/>
				)}
			</BookingSummaryLayout>
		);
	},
);
